import { useEffect, useState } from 'react'

import {
  fetchDistinctFieldValues as fetchDistinctFieldValuesWith,
  fetchReferenceListOptions as fetchReferenceListOptionsWith,
  IReferenceListOption,
} from 'shared-libs/helpers/reference-list'

import apis from 'browser/app/models/apis'

/**
 * Web binding for the platform-agnostic option-source resolver
 * (shared-libs/helpers/reference-list). Supplies the browser `apis` instance
 * and React hooks; the pure logic (mapping, key/firm/source extraction,
 * transform) is re-exported from the shared module so callers have a single
 * import site.
 */

export {
  getOptionSource,
  getReferenceListKey,
  getEntityOwnerFirmId,
  resolveEntityTypeId,
  applyTagTransform,
  mapReferenceListItemsToOptions,
  IReferenceListOption,
  IOptionSource,
} from 'shared-libs/helpers/reference-list'

/** Loads a firm's reference list by key, scoped to `firmId` (the entity owner firm) when given. */
export const fetchReferenceListOptions = (
  referenceListKey?: string,
  firmId?: string,
): Promise<IReferenceListOption[]> => fetchReferenceListOptionsWith(apis, referenceListKey, firmId)

/** Loads the firm's distinct existing values for a bound field. */
export const fetchDistinctFieldValues = (
  entityType: string,
  valuePath: string[],
  size?: number,
): Promise<IReferenceListOption[]> => fetchDistinctFieldValuesWith(apis, entityType, valuePath, size)

export interface IUseReferenceListOptions {
  options: IReferenceListOption[]
  isLoading: boolean
  /** true once a load attempt has settled (so callers know when [] means "no list"). */
  loaded: boolean
}

/**
 * Hook wrapper around {@link fetchReferenceListOptions}. Loads eagerly on mount
 * (and when the key or firm changes) so options are ready before the menu opens.
 */
export const useReferenceListOptions = (
  referenceListKey?: string,
  firmId?: string,
): IUseReferenceListOptions => {
  const [options, setOptions] = useState<IReferenceListOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!referenceListKey) {
      setOptions([])
      setLoaded(true)
      return
    }
    setLoaded(false)
    setIsLoading(true)
    fetchReferenceListOptions(referenceListKey, firmId)
      .then((opts) => {
        if (!cancelled) {
          setOptions(opts)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
          setLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [referenceListKey, firmId])

  return { options, isLoading, loaded }
}

/**
 * Hook that loads the firm's distinct existing values for a bound field. Pass a
 * falsy `entityType` (or empty `valuePath`) to skip the load — callers use this
 * to only fetch distinct values when `optionSource.distinctValues` is enabled
 * and no reference list supplied options.
 */
export const useDistinctFieldValues = (
  entityType: string | undefined,
  valuePath: string[],
  size?: number,
): IUseReferenceListOptions => {
  const [options, setOptions] = useState<IReferenceListOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const valuePathKey = (valuePath ?? []).join('.')

  useEffect(() => {
    let cancelled = false
    if (!entityType || !valuePathKey) {
      setOptions([])
      setLoaded(true)
      return
    }
    setLoaded(false)
    setIsLoading(true)
    fetchDistinctFieldValues(entityType, valuePathKey.split('.'), size)
      .then((opts) => {
        if (!cancelled) {
          setOptions(opts)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
          setLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [entityType, valuePathKey, size])

  return { options, isLoading, loaded }
}
